import React, { Component } from "react";
import { Link, withRouter } from "react-router-dom";
import { connect } from "react-redux";
import axios from "axios";
import { Store } from "react-notifications-component";
import { Plus, Pencil, Trash2, GripVertical, Feather } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import setAuthToken from "../../utils/setAuthToken";
import confirmAlert from "../../utils/confirmAlert";
import { logoutUser } from "../../actions/authActions";
import { toggleDarkMode } from "../../actions/preferenceActions";
import AdminCommandPalette from "./AdminCommandPalette";
import AdminNotifications from "./AdminNotifications";

import {
  AdminAppShell,
  PageHeader,
  Button,
  IconButton,
  Tooltip,
  Spinner,
  EmptyState,
  SearchInput,
  Breadcrumb,
} from "../../design-system";
import { buildAdminBreadcrumb } from "../../utils/adminBreadcrumb";

/* -------------------------------------------------------------------------- */
/*  Composants fonctionnels — utilisent les hooks dnd-kit                     */
/* -------------------------------------------------------------------------- */

function SortableStageRow({ stage, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: stage.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr ref={setNodeRef} style={style} {...attributes} className="hover:bg-bg-2/50 transition-colors">
      <td className="w-10 px-3 py-3 align-middle">
        <button
          type="button"
          {...listeners}
          aria-label="Glisser pour réordonner"
          className="cursor-grab active:cursor-grabbing p-1 text-mute hover:text-text"
        >
          <GripVertical className="w-4 h-4" strokeWidth={1.8} />
        </button>
      </td>
      <td className="px-3 py-3 align-middle">
        <div className="font-semibold text-text leading-tight">{stage.title}</div>
      </td>
      <td className="px-3 py-3 align-middle text-sm">{stage.company || "—"}</td>
      <td className="px-3 py-3 align-middle text-sm text-mute font-mono">{stage.period || "—"}</td>
      <td className="px-3 py-3 align-middle text-sm font-mono tabular-nums text-mute w-20 text-right">{stage.order}</td>
      <td className="px-3 py-3 align-middle w-32">
        <div className="flex items-center gap-0.5 justify-end">
          <Tooltip content="Modifier">
            <IconButton
              as={Link}
              to={`/dashboard/stages/${stage.id}`}
              size="sm"
              variant="ghost"
              icon={<Pencil strokeWidth={1.8} />}
              aria-label={`Modifier ${stage.title}`}
            />
          </Tooltip>
          <Tooltip content="Supprimer">
            <IconButton
              size="sm"
              variant="ghost"
              icon={<Trash2 strokeWidth={1.8} />}
              aria-label={`Supprimer ${stage.title}`}
              onClick={() => onDelete(stage)}
            />
          </Tooltip>
        </div>
      </td>
    </tr>
  );
}

function SortableStageTable({ stages, onDragEnd, onDelete }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  return (
    <div className="rounded-lg bg-cream overflow-hidden border border-ink/12 overflow-x-auto">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <table className="w-full text-sm border-collapse">
          <thead className="bg-ink text-cream">
            <tr>
              <th className="w-10 px-3 py-2.5"></th>
              <th className="text-left px-3 py-2.5 text-[10.5px] uppercase tracking-wider font-semibold">Titre</th>
              <th className="text-left px-3 py-2.5 text-[10.5px] uppercase tracking-wider font-semibold">Entreprise</th>
              <th className="text-left px-3 py-2.5 text-[10.5px] uppercase tracking-wider font-semibold">Période</th>
              <th className="text-right px-3 py-2.5 text-[10.5px] uppercase tracking-wider font-semibold w-20">Ordre</th>
              <th className="w-32 px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink/8">
            <SortableContext items={stages.map((s) => s.id)} strategy={verticalListSortingStrategy}>
              {stages.map((stage) => (
                <SortableStageRow key={stage.id} stage={stage} onDelete={onDelete} />
              ))}
            </SortableContext>
          </tbody>
        </table>
      </DndContext>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Class principale                                                          */
/* -------------------------------------------------------------------------- */

class StageList extends Component {
  constructor(props) {
    super(props);
    this.state = { stages: [], loading: true };
  }

  componentDidMount() {
    this.loadStages();
  }

  loadStages = async () => {
    try {
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const response = await axios.get(`/api/stages`);
      this.setState({ stages: response.data.stages || [], loading: false });
    } catch (error) {
      console.error("Error loading stages:", error);
      this.setState({ loading: false });
      Store.addNotification({
        title: "Erreur",
        message: "Impossible de charger les stages",
        type: "danger",
        insert: "top",
        container: "top-right",
        dismiss: { duration: 3000 },
      });
    }
  };

  handleDelete = (stage) => {
    confirmAlert({
      title: "Supprimer ce stage ?",
      message: `« ${stage.title} » sera supprimé définitivement.`,
      type: "danger",
      confirmText: "Supprimer",
      cancelText: "Annuler",
      onConfirm: async () => {
        try {
          const token = localStorage.getItem("jwtToken");
          if (token) setAuthToken(token);
          await axios.delete(`/api/stages/${stage.id}`);
          Store.addNotification({
            title: "Stage supprimé",
            message: `« ${stage.title} » a été retiré.`,
            type: "success",
            insert: "top", container: "top-right", dismiss: { duration: 3000 },
          });
          this.loadStages();
        } catch (error) {
          console.error("Error deleting stage:", error);
          Store.addNotification({
            title: "Erreur",
            message: "Impossible de supprimer le stage",
            type: "danger",
            insert: "top", container: "top-right", dismiss: { duration: 3000 },
          });
        }
      },
    });
  };

  handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = this.state.stages.findIndex((s) => s.id === active.id);
    const newIndex = this.state.stages.findIndex((s) => s.id === over.id);
    const newStages = arrayMove(this.state.stages, oldIndex, newIndex);
    this.setState({ stages: newStages });

    try {
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const orderList = newStages.map((stage, index) => ({ id: stage.id, order: index }));
      await axios.put("/api/stages/reorder", { orderList });
      this.setState({ stages: newStages.map((s, i) => ({ ...s, order: i })) });
      Store.addNotification({
        title: "Ordre mis à jour",
        type: "success",
        insert: "top", container: "top-right", dismiss: { duration: 2000 },
      });
    } catch (error) {
      console.error("Error reordering stages:", error);
      this.loadStages();
      Store.addNotification({
        title: "Erreur",
        message: "Impossible de mettre à jour l'ordre",
        type: "danger",
        insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
    }
  };

  onLogout = () => {
    confirmAlert({
      title: "Déconnexion",
      message: "Êtes-vous sûr de vouloir vous déconnecter ?",
      type: "warning",
      confirmText: "Déconnexion",
      cancelText: "Annuler",
      onConfirm: () => {
        this.props.logoutUser();
        this.props.history.push("/login");
      },
    });
  };

  onToggleDarkMode = () => {
    const { _preferences } = this.props;
    this.props.toggleDarkMode(!_preferences?._dark);
  };

  render() {
    const { stages, loading } = this.state;
    const { user, _preferences, location } = this.props;

    return (
      <AdminAppShell
        user={user}
        currentPath={location.pathname}
        isDarkMode={_preferences?._dark || false}
        onToggleDarkMode={this.onToggleDarkMode}
        onLogout={this.onLogout}
        LinkComponent={Link}
        pageTitle="Stages"
        breadcrumb={<Breadcrumb items={buildAdminBreadcrumb(location.pathname)} LinkComponent={Link} />}
        topbarCenter={<AdminCommandPalette />}
        topbarRight={<AdminNotifications />}
      >
        <div className="w-full px-5 sm:px-6 lg:px-8 xl:px-10 py-6 lg:py-8 space-y-6 lg:space-y-7">
          <PageHeader
            size="hero"
            eyebrow="— Section iii · Stages"
            title={<>Le parcours, <span className="font-serif italic font-normal align-baseline">en ordre.</span></>}
            description="Glisse les lignes pour réordonner — c'est la chronologie qui apparaît sur le site."
            actions={
              <Button
                as={Link}
                to="/dashboard/stages/new"
                leftIcon={<Feather className="w-4 h-4" strokeWidth={1.8} />}
              >
                Nouveau stage
              </Button>
            }
          />

          {loading ? (
            <div className="flex items-center justify-center py-20 text-mute">
              <Spinner size="lg" />
            </div>
          ) : stages.length === 0 ? (
            <EmptyState
              title="Aucun stage pour le moment"
              description="Ajoute ton premier stage : entreprise, période, contexte."
              action={<Button as={Link} to="/dashboard/stages/new" leftIcon={<Plus className="w-4 h-4" />}>Nouveau stage</Button>}
            />
          ) : (
            <SortableStageTable stages={stages} onDragEnd={this.handleDragEnd} onDelete={this.handleDelete} />
          )}
        </div>
      </AdminAppShell>
    );
  }
}

const mapStateToProps = (state) => ({
  user: state.auth.user,
  _preferences: state._preferences,
});

export default connect(mapStateToProps, { logoutUser, toggleDarkMode })(withRouter(StageList));
